<script setup lang="ts">
import ReQrcode from '@/components/ReQrcode'
import weixinIcon from '@/assets/icon/weixin.png'
import zfbIcon from '@/assets/icon/zhifubao.png'
import userApi from '@/api/userApi'
import { Code } from '@/config/httpCode'
import { ReqCreateOrder } from '@/api/type'
import { WarningFilled } from '@element-plus/icons-vue'
import { ref, watch, computed } from 'vue'
import { PayChannel } from '@/config/constants'
import { message } from '@/utils/message'
import type { WsResponse } from '@/utils/websocket'
import { WebScoketOnMessage } from '@/types/constantsEnum'
import { WebSocketEvent } from '@/types/constantsEnum'
import { useWebSocketStore } from '@/store/modules/websocket'
import integralApi from '@/api/integralApi'
import { getBenefitValue } from '@/utils/commonUtils'
import { UserBenefitType } from '@/types/constantsEnum'
import { OrderStatus } from '@/config/constants'
import { useUserStoreHook } from '@/store/modules/user'
import { Navigation } from '@/utils/navigation'
const wsStore = useWebSocketStore()
const userStore = useUserStoreHook()

const props = defineProps<{
  visible: boolean
  payInfo: ReqCreateOrder
}>()

const emit = defineEmits(['update:visible', 'close'])
const dialogVisible = ref(props.visible)
const currentPayChannel = ref(PayChannel.WEIXIN)
const codeInfo = ref<string>(props.payInfo.qrCode || '')
const loading = ref(false)
const orderId = ref<string>(props.payInfo.orderId || '')
const isGif = ref(true)
const iswsSuccess = ref(false)
// 确保二维码有有效内容
const qrCodeText = computed(() => {
  return codeInfo.value || ''
})

watch(
  () => props.visible,
  (newVal) => {
    dialogVisible.value = newVal
  },
)

watch(
  () => props.payInfo,
  (newVal) => {
    if (newVal && newVal.qrCode) {
      orderId.value = newVal.orderId
      codeInfo.value = newVal.qrCode
    }
  },
  { deep: true },
)

watch(
  () => dialogVisible.value,
  (newVal) => {
    emit('update:visible', newVal)
    if (!newVal) {
      emit('close')
    }
  },
)

const showMask = ref<boolean>(false)
const handleRefreshPayStatus = async () => {
  iswsSuccess.value = false

  try {
    const res = await userApi.refreshPayStatus(orderId.value)
    if (res.statusCode === Code.SUCCESS.statusCode) {
      // 更新积分余额
      const integareInfo = await integralApi.getUserBenefit()
      if (
        integareInfo.statusCode === Code.SUCCESS.statusCode &&
        integareInfo.data.orderStatus === OrderStatus.SUCCESS
      ) {
        const creditValue = getBenefitValue(integareInfo.data, UserBenefitType.CREDIT)
        const scoreDetail = {
          credit: creditValue,
        }
        userStore.setUserData('scoreDetail', scoreDetail)
      }
    }
  } catch (error) {
    console.error('刷新支付状态失败:', error)
  } finally {
    dialogVisible.value = false
  }
}

const handleChangePay = async (channel: number) => {
  if (currentPayChannel.value !== channel) {
    currentPayChannel.value = channel
    loading.value = true
    try {
      const res = await userApi.createOrder({
        packId: props.payInfo.packId,
        purchaseCount: props.payInfo.purchaseCount,
        channel: channel,
      })
      console.log(res, 'res')

      if (res.statusCode === Code.SUCCESS.statusCode) {
        const payData = res.data
        orderId.value = payData.orderId
        codeInfo.value = payData.qrCode
        if (channel === PayChannel.ZHIFUBAO) {
          Navigation.openBlank(payData.qrCode)
        }
      } else {
        console.error('创建订单失败:', res.message)
      }
    } catch (error) {
      console.error('创建订单失败:', error)
    } finally {
      loading.value = false
    }
  }
}
const startGzQrDataInterval = async () => {
  showMask.value = true
  loading.value = true

  try {
    const res = await userApi.createOrder({
      packId: props.payInfo.packId,
      purchaseCount: props.payInfo.purchaseCount,
      channel: currentPayChannel.value,
    })

    if (res.statusCode === Code.SUCCESS.statusCode) {
      const payData = res.data
      codeInfo.value = payData.qrCode
      if (currentPayChannel.value === PayChannel.ZHIFUBAO) {
        Navigation.openBlank(payData.qrCode)
      }
    } else {
      message.error('刷新支付二维码失败，请稍后重试')
    }
  } catch (error) {
    console.error('刷新支付二维码失败:', error)
    message.error('刷新支付二维码失败，请稍后重试')
  } finally {
    loading.value = false
    showMask.value = false
  }
}
const handleMessage = (data: WsResponse) => {
  console.log(data, 'data接收到消息了++++')
  if (
    data.event === WebSocketEvent.UserEvent &&
    data.data.type === WebScoketOnMessage.OrderSuccess
  ) {
    iswsSuccess.value = true
    setTimeout(() => {
      isGif.value = false
    }, 2010)
    // dialogVisible.value = false
  }
}

onMounted(() => {
  wsStore.addMessageListener(WebSocketEvent.UserEvent, handleMessage)
})

onUnmounted(() => {
  wsStore.removeMessageListener(WebSocketEvent.UserEvent, handleMessage)
})
</script>
<template>
  <el-dialog v-model="dialogVisible" width="446px" top="30vh" @close="handleRefreshPayStatus">
    <!-- 支付状态 -->
    <div class="flex flex-col items-center justify-center w-full" v-if="!iswsSuccess">
      <div class="flex flex-row items-center justify-center w-full">
        <div
          class="flex flex-row items-center justify-center h-[29px] border-[#E5E5EA] border-solid border-[0.5px] rounded-[3px] bg-[#fff] w-[113px] mr-[20px] transition-all duration-300 ease-in-out hover:shadow-md payment-method-button"
          :class="{
            '!border-[#0770ED] !bg-[#F0F8FF] active': currentPayChannel === PayChannel.WEIXIN,
          }"
          @click="handleChangePay(PayChannel.WEIXIN)"
        >
          <img
            src="@/assets/icon/weixin.png"
            alt="bg"
            class="w-[15px] h-[15px] transition-transform duration-300 relative z-10"
            :class="{ 'scale-110': currentPayChannel === PayChannel.WEIXIN }"
          />
          <div
            class="text-size14 ml-[7px] transition-colors duration-300 relative z-10"
            :class="{
              'text-[#0770ED] font-medium': currentPayChannel === PayChannel.WEIXIN,
              'text-[#7C7777]': currentPayChannel !== PayChannel.WEIXIN,
            }"
          >
            微信支付
          </div>
        </div>
        <div
          class="flex flex-row items-center justify-center h-[29px] border-[#E5E5EA] border-solid border-[0.5px] rounded-[3px] bg-[#fff] w-[113px] transition-all duration-300 ease-in-out hover:shadow-md payment-method-button"
          :class="{
            '!border-[#0770ED] !bg-[#F0F8FF] active': currentPayChannel === PayChannel.ZHIFUBAO,
          }"
          @click="handleChangePay(PayChannel.ZHIFUBAO)"
        >
          <img
            src="@/assets/icon/zhifubao.png"
            alt="bg"
            class="w-[15px] h-[15px] transition-transform duration-300 relative z-10"
            :class="{ 'scale-110': currentPayChannel === PayChannel.ZHIFUBAO }"
          />
          <div
            class="text-size14 ml-[7px] transition-colors duration-300 relative z-10"
            :class="{
              'text-[#0770ED] font-medium': currentPayChannel === PayChannel.ZHIFUBAO,
              'text-[#7C7777]': currentPayChannel !== PayChannel.ZHIFUBAO,
            }"
          >
            支付宝支付
          </div>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-center w-full mt-[30px]"
        v-loading="loading"
        v-if="currentPayChannel === PayChannel.WEIXIN"
      >
        <div
          class="flex w-[147px] h-[147px] items-center justify-center border-[0.5px] border-[#C9C9C9] rounded-[5px] qrcode-container relative overflow-hidden"
        >
          <ReQrcode
            :logo="currentPayChannel === PayChannel.WEIXIN ? weixinIcon : zfbIcon"
            :options="{
              color: {
                dark: '#000000',
                light: '#ffffff',
              },
            }"
            :width="130"
            :text="qrCodeText"
          />
          <div v-if="showMask" class="qrcode-mask" @click="startGzQrDataInterval">
            <el-icon size="3rem" color="#e69b5e" class="mb-[1.2rem]">
              <WarningFilled />
            </el-icon>
            <div>二维码失效,点击刷新</div>
          </div>
        </div>
      </div>
      <div
        class="flex items-center justify-center w-full mt-[20px] text-size20 font-[500] text-[#3D3D3D]"
      >
        应付金额：<span class="text-size20 font-[500] text-[#f56c6c]">￥{{ payInfo.price }}</span>
      </div>
      <div class="flex flex-col mt-[30px] h-full justify-center items-center mb-[20px]">
        <div
          class="font-roboto font-[500] px-[18px] py-[8px] bg-home-aisound-btn rounded-[5px] text-size16 text-white hover-transition pay-button"
          @click="handleRefreshPayStatus"
        >
          我已支付
        </div>
      </div>
    </div>
    <!-- sokect成功状态 -->
    <div class="flex flex-col items-center justify-center w-full h-[300px]" v-if="iswsSuccess">
      <div class="flex flex-col items-center justify-center w-full">
        <div class="w-[79px] h-[79px] flex items-center justify-center">
          <img
            v-if="isGif"
            src="@/assets/icon/success.gif"
            alt="success"
            class="w-[79px] h-[79px]"
          />
          <img
            v-else
            src="@/assets/icon/success_static.png"
            alt="success"
            class="w-[48px] h-[48px]"
          />
        </div>
        <div class="text-size16 font-roboto font-[500] text-[rgb(66,210,150)] mt-[20px]">
          支付成功
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped>
.hover-transition {
  transition: all 0.3s ease;
}

/* 支付方式选择按钮样式 */
.payment-method-button {
  position: relative;
  overflow: hidden;
}

.payment-method-button::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(7, 112, 237, 0.08);
  transform: translateX(-100%);
  transition: transform 0.3s ease-out;
  z-index: 0;
}

.payment-method-button.active::after {
  transform: translateX(0);
}

/* 二维码容器样式 */
.qrcode-container {
  position: relative;
  transition: all 0.3s ease;
}

.qrcode-container:hover {
  transform: scale(1.02);
}

.qrcode-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.qrcode-container:hover::after {
  opacity: 1;
}

.qrcode-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(236, 234, 234, 0.9);
  backdrop-filter: blur(2px);
  transition: all 0.3s ease;
  cursor: pointer;
}

.qrcode-mask:hover {
  background-color: rgba(236, 234, 234, 0.95);
}

/* 对话框动画 */
:deep(.el-overlay-dialog) {
  transition: opacity 0.3s ease-in-out !important;
}

:deep(.el-dialog) {
  transition: transform 0.3s ease-in-out !important;
  transform: translateY(0) !important;
  margin-top: 0 !important;
}

:deep(.el-dialog__header) {
  margin-right: 0;
}

/* 我已支付按钮 */
.pay-button {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.pay-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.pay-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
